import React from "react";
import { motion } from "framer-motion";
import { Target, Users, Globe, Award, Code2, Brain, Palette, LineChart } from "lucide-react";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";

const ABOUT_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "About — Spin Labs",
    url: "https://spinlabs.in/about",
    description:
      "Spin Labs is a remote-first software studio founded in 2025 in Delhi, building outsized outcomes for ambitious teams.",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Spin Labs",
    url: "https://spinlabs.in",
    logo: "https://spinlabs.in/logo.png",
    foundingDate: "2025",
    foundingLocation: { "@type": "Place", name: "Delhi, IN" },
    address: { "@type": "PostalAddress", addressLocality: "Delhi", addressCountry: "IN" },
    knowsAbout: [
      "Product Engineering",
      "Applied AI",
      "Design and Motion",
      "Growth and SEO",
    ],
  },
  breadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
  ]),
];

const VALUES = [
  { i: Target, t: "Clarity Over Noise", d: "We strip the fluff, kill the buzzwords, and ship what actually moves the metric." },
  { i: Users, t: "Humans First", d: "We design for the people who use the product — not the ones who sign off on it." },
  { i: Globe, t: "Global by Default", d: "Every app we build is localized, accessible, and ready for 40M+ users from day one." },
  { i: Award, t: "Craft Is Our Religion", d: "Pixel-perfect, millisecond-fast, 99.99% uptime. No excuses. No shortcuts." },
];

const DISCIPLINES = [
  { i: Code2, t: "Product Engineering", d: "Full-stack web, mobile, and platform engineers with a decade of shipping experience between them." },
  { i: Brain, t: "Applied AI", d: "RAG, agents, fine-tuning, and evaluation — built for production, not demos." },
  { i: Palette, t: "Design & Motion", d: "Interaction design, interface systems, and motion craft that make products feel inevitable." },
  { i: LineChart, t: "Growth & SEO", d: "Technical SEO, paid media, lifecycle automation, and the analytics to prove it worked." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About — A Studio Building Outsized Outcomes"
        path="/about"
        description="Spin Labs is a Delhi-based remote-first software studio crafting AI, mobile, SaaS and software products for ambitious teams worldwide."
        keywords="Spin Labs team, Delhi software studio, remote software agency, AI studio India, founders Spin Labs"
        jsonLd={ABOUT_JSONLD}
      />
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="spotlight w-[600px] h-[600px] bg-[#FF00FF]/15 top-0 left-0" />
        <div className="spotlight w-[600px] h-[600px] bg-[#00F0FF]/15 bottom-0 right-0" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="tag-chip mb-6">About Us</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-black tracking-[-0.04em] leading-[0.9] text-balance max-w-5xl">
              A small studio building <span className="gradient-text">outsized</span> outcomes.
            </h1>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <p className="text-lg text-white/70 leading-relaxed">
                Spin Labs started in 2025 as a small crew of engineers and designers frustrated with agency theatre.
                We wanted a studio that shipped real software — not decks, not "frameworks", not think pieces.
              </p>
              <p className="mt-6 text-lg text-white/70 leading-relaxed">
                Today we're a tight remote-first team based in Delhi with collaborators around the world, helping fast-moving
                teams launch products people actually use. We bet on AI early, we bet on craft always, and we bet on our clients becoming
                competitors to their industries.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="grid grid-cols-2 gap-4">
              {[
                { k: "2025", v: "Founded" },
                { k: "Delhi", v: "Home base" },
                { k: "Global", v: "Clients" },
                { k: "24h", v: "Reply SLA" },
              ].map((s, i) => (
                <div key={i} className="p-5 sm:p-7 lg:p-8 border border-white/10 rounded-2xl hover:border-[#00F0FF]/40 transition-colors min-w-0 overflow-hidden">
                  <div className="font-display text-2xl sm:text-3xl lg:text-[40px] font-black gradient-text leading-none">{s.k}</div>
                  <div className="mt-3 font-mono-neo text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] text-white/50">{s.v}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-24 bg-[#07070A] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="tag-chip mb-4">Values</div>
          <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter max-w-3xl">
            Four rules we won't break.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="p-8 border border-white/10 rounded-2xl hover:border-[#00F0FF]/40 transition-colors"
              >
                <v.i className="w-6 h-6 text-[#00F0FF] mb-4" />
                <h3 className="font-display text-2xl font-bold mb-3">{v.t}</h3>
                <p className="text-white/60 leading-relaxed">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Disciplines */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="tag-chip mb-4">The Core</div>
          <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter max-w-3xl">
            Senior, specialized, <span className="gradient-text">unreasonably good.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-white/60 leading-relaxed">
            Founder-led and senior-only. Every project gets the people who built the last one — no juniors learning on your budget.
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {DISCIPLINES.map((m, i) => (
              <motion.div
                key={m.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative rounded-2xl p-8 border border-white/10 hover:border-[#00F0FF]/40 transition-colors bg-[#0A0A0C]"
              >
                <m.i className="w-6 h-6 text-[#00F0FF] mb-5" />
                <div className="font-display font-bold text-xl">{m.t}</div>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{m.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
